
import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format, isValid, parseISO } from "date-fns";

interface TaskFormFieldsProps {
  selectedTask: any;
  handleChange: (field: string, value: any) => void;
  selectedProject: any;
  calendarOpen: boolean;
  setCalendarOpen: (open: boolean) => void;
  calendarEndOpen: boolean;
  setCalendarEndOpen: (open: boolean) => void;
  isRestrictedUser?: boolean;
}

// Helper function to safely parse and validate dates
const parseDate = (dateValue: any): Date | null => {
  if (!dateValue) return null;
  
  let date: Date;
  
  if (dateValue instanceof Date) {
    date = dateValue;
  } else if (typeof dateValue === 'string') {
    // Try parsing ISO format first
    if (dateValue.includes('T') || dateValue.includes('-')) {
      date = parseISO(dateValue);
    } else {
      date = new Date(dateValue);
    }
  } else {
    return null;
  }
  
  return isValid(date) ? date : null;
};

// Helper function to safely format dates
const formatDate = (dateValue: any, formatString: string = 'dd MMM yyyy'): string => {
  const parsedDate = parseDate(dateValue);
  if (!parsedDate) return "Select date";
  
  try {
    return format(parsedDate, formatString);
  } catch (error) {
    console.error('Date formatting error:', error);
    return "Select date";
  }
};

export const TaskFormFields: React.FC<TaskFormFieldsProps> = ({
  selectedTask,
  handleChange,
  selectedProject,
  calendarOpen,
  setCalendarOpen,
  calendarEndOpen,
  setCalendarEndOpen,
  isRestrictedUser = false
}) => {
  return (
    <>
      <div className="grid grid-cols-1 gap-2">
        <Label htmlFor="name">Task Name</Label>
        <Input
          id="name"
          value={selectedTask.name || ''}
          onChange={(e) => handleChange('name', e.target.value)}
          disabled={isRestrictedUser}
          className={`${
            isRestrictedUser 
              ? "bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed" 
              : "bg-white border-gray-300"
          }`}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="startDate" className="text-black">Start Date</Label>
          <Popover open={isRestrictedUser ? false : calendarOpen} onOpenChange={isRestrictedUser ? () => {} : setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                id="startDate"
                variant="outline"
                disabled={isRestrictedUser}
                className={`w-full justify-start text-left font-normal ${
                  isRestrictedUser
                    ? "bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-white border-gray-300"
                }`}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {formatDate(selectedTask.startDate)}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-white" align="start">
              <Calendar
                mode="single"
                selected={parseDate(selectedTask.startDate) || undefined}
                onSelect={(date) => {
                  if (date) {
                    handleChange('startDate', format(date, 'dd MMM yyyy'));
                    setCalendarOpen(false);
                  }
                }}
                initialFocus
                className="p-3 pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
        </div>

        <div>
          <Label htmlFor="duration" className="text-black">Duration (Days)</Label>
          <Input
            id="duration"
            type="number"
            value={selectedTask.duration || 0}
            onChange={(e) => handleChange('duration', e.target.value)}
            disabled={isRestrictedUser}
            className={`${
              isRestrictedUser
                ? "bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-white border-gray-300"
            }`}
            min="0"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="endDate" className="text-black">End Date</Label>
        <Button
          id="endDate"
          variant="outline"
          disabled={true}
          className="w-full justify-start text-left font-normal bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed"
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {formatDate(selectedTask.endDate)}
        </Button>
      </div>

      <div>
        <Label htmlFor="progress" className="text-black">Progress (%)</Label>
        <Input
          id="progress"
          type="number"
          value={selectedTask.progress || 0}
          onChange={(e) => handleChange('progress', e.target.value)}
          disabled={isRestrictedUser}
          className={`${
            isRestrictedUser 
              ? "bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed" 
              : "bg-white border-gray-300"
          }`}
          min="0"
          max="100"
        />
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox 
          id="isCritical"
          checked={selectedTask.isCritical || false}
          onCheckedChange={(checked) => handleChange('isCritical', checked)}
          disabled={isRestrictedUser}
        />
        <Label htmlFor="isCritical" className="text-black">Mark as critical path task</Label>
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox 
          id="excludeWeekends"
          checked={selectedProject?.excludeWeekends || false}
          disabled={true}
        />
        <Label htmlFor="excludeWeekends" className="text-black">Exclude weekends when projecting date and duration</Label>
      </div>
    </>
  );
};
